import { MapPin, Phone, Mail, Clock, Facebook, Instagram, Download } from 'lucide-react';

export default function Footer() {
  const currentYear = new Date().getFullYear();

  const footerSections = [
    {
      title: "Nos Services",
      links: [
        { label: "Activités à Dubaï", href: "/activites" },
        { label: "Hôtels de luxe", href: "/hotels" },
        { label: "Chauffeur privé", href: "/transport/chauffeur" },
        { label: "Devis personnalisé", href: "/devis" },
        { label: "Conseils voyage", href: "/conseils" }
      ]
    },
    {
      title: "Activités Populaires",
      links: [
        { label: "Safari dans le désert", href: "/activites/desert" },
        { label: "Burj Khalifa", href: "/activites/burj-khalifa" },
        { label: "Parcs aquatiques", href: "/activites/parcs" },
        { label: "Croisières", href: "/activites/mer" },
        { label: "Expériences VIP", href: "/activites/vip" }
      ]
    },
    {
      title: "Informations",
      links: [
        { label: "Qui sommes-nous", href: "/qui-sommes-nous" },
        { label: "FAQ", href: "/faq" },
        { label: "Conditions générales", href: "/cgv" },
        { label: "Politique de confidentialité", href: "/confidentialite" },
        { label: "Mentions légales", href: "/mentions-legales" }
      ]
    }
  ];

  const socialLinks = [
    {
      icon: Facebook,
      href: "https://www.facebook.com/mesvacancesadubai",
      label: "Facebook"
    },
    {
      icon: Instagram,
      href: "https://www.instagram.com/mesvacancesadubai",
      label: "Instagram"
    }
  ];

  return (
    <footer className="bg-gray-900 text-white">
      {/* Main footer content */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Company info */}
          <div className="lg:col-span-1">
            <div className="mb-6">
              <img 
                src="https://mesvacancesadubai.com/wp-content/uploads/2021/02/MVD-Logo-Dubai-Color.png" 
                alt="Mes Vacances à Dubaï" 
                className="h-12 w-auto mb-4"
              />
              <p className="text-gray-300 leading-relaxed">
                Votre partenaire de confiance pour découvrir Dubaï. 
                Équipe française basée à Dubaï depuis plus de 10 ans.
              </p>
            </div>

            {/* Contact info */}
            <div className="space-y-3 mb-6">
              <div className="flex items-center space-x-3">
                <MapPin size={16} className="text-amber-400 flex-shrink-0" />
                <span className="text-sm text-gray-300">
                  Dubai Silicon Oasis, Dubai, UAE
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <Phone size={16} className="text-amber-400 flex-shrink-0" />
                <a 
                  href="tel:+971506211791" 
                  className="text-sm text-gray-300 hover:text-amber-400 transition-colors"
                >
                  +971 50 62 11 791
                </a>
              </div>
              <div className="flex items-center space-x-3">
                <Mail size={16} className="text-amber-400 flex-shrink-0" />
                <a 
                  href="mailto:contact@mvd.perle-in.com" 
                  className="text-sm text-gray-300 hover:text-amber-400 transition-colors"
                >
                  contact@mvd.perle-in.com
                </a>
              </div>
              <div className="flex items-center space-x-3">
                <Clock size={16} className="text-amber-400 flex-shrink-0" />
                <span className="text-sm text-gray-300">
                  7j/7 - 9h à 21h (heure de Dubaï)
                </span>
              </div>
            </div>

            {/* Social links */}
            <div className="flex space-x-4">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-amber-600 transition-colors"
                  aria-label={social.label}
                >
                  <social.icon size={20} />
                </a>
              ))}
            </div>
          </div>

          {/* Footer sections */}
          {footerSections.map((section, index) => (
            <div key={index}>
              <h3 className="text-lg font-semibold mb-6 text-amber-400">
                {section.title}
              </h3>
              <ul className="space-y-3">
                {section.links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <a
                      href={link.href}
                      className="text-gray-300 hover:text-amber-400 transition-colors text-sm"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Newsletter & Brochure */}
        <div className="border-t border-gray-800 mt-12 pt-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Newsletter */}
            <div>
              <h3 className="text-lg font-semibold mb-4 text-amber-400">
                Restez informé
              </h3>
              <p className="text-gray-300 text-sm mb-4">
                Recevez nos offres exclusives et conseils d'experts
              </p>
              <div className="flex">
                <input
                  type="email"
                  placeholder="Votre email"
                  className="flex-1 px-4 py-2 bg-gray-800 border border-gray-700 rounded-l-lg focus:outline-none focus:border-amber-400 text-white"
                />
                <button className="bg-amber-600 hover:bg-amber-700 px-6 py-2 rounded-r-lg transition-colors">
                  S'inscrire
                </button>
              </div>
            </div>

            {/* Brochure */}
            <div>
              <h3 className="text-lg font-semibold mb-4 text-amber-400">
                Téléchargez notre brochure
              </h3>
              <p className="text-gray-300 text-sm mb-4">
                Découvrez toutes nos activités et offres spéciales
              </p>
              <a
                href="https://mesvacancesadubai.com/wp-content/uploads/2025/01/brochure-2025.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 px-6 py-2 rounded-lg transition-colors"
              >
                <Download size={16} />
                <span>Télécharger PDF</span>
              </a>
            </div>
          </div>
        </div>

        {/* Trust indicators */}
        <div className="border-t border-gray-800 mt-8 pt-8">
          <div className="flex flex-wrap justify-center items-center gap-8 text-center">
            <div className="flex items-center space-x-2">
              <img 
                src="https://mesvacancesadubai.com/wp-content/uploads/2024/11/card.png" 
                alt="Paiement sécurisé" 
                className="h-8"
              />
              <span className="text-sm text-gray-400">Paiement sécurisé</span>
            </div>
            <div className="text-sm text-gray-400">
              🏆 +100k clients satisfaits
            </div>
            <div className="text-sm text-gray-400">
              ⭐ Note 4.9/5 sur Google
            </div>
            <div className="text-sm text-gray-400">
              🇫🇷 Équipe française
            </div>
            <div className="text-sm text-gray-400">
              🛡️ Licence Dubai Tourism
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-sm text-gray-400 text-center md:text-left">
              <div>© {currentYear} <strong>Mes Vacances à Dubaï</strong> - Tous droits réservés</div>
              <div className="mt-2 text-xs text-gray-500">
                Site réalisé et propulsé par <strong className="text-amber-400">PERLE IN</strong>
              </div>
            </div>
            <div className="flex flex-wrap justify-center md:justify-end items-center space-x-6 text-sm text-gray-400">
              <a href="/mentions-legales" className="hover:text-amber-400 transition-colors">
                Mentions légales
              </a>
              <a href="/confidentialite" className="hover:text-amber-400 transition-colors">
                Confidentialité
              </a>
              <a href="/cgv" className="hover:text-amber-400 transition-colors">
                CGV
              </a>
              <a href="/faq" className="hover:text-amber-400 transition-colors">
                FAQ
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}